import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { calculateSortOrder, toastManager } from "@salestrak/ui";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  BulkDropIssuesToStageDocument,
  DeleteIssueDocument,
  type IssueRow,
  MoveIssueToStageDocument,
  ReorderIssueDocument,
} from "#app/lib/data";
import {
  removeIssueFromStageCache,
  insertIssueIntoStageCache,
  reorderIssueInStageCache,
  dropIssuesIntoStageCache,
} from "./kanban-cache-utils";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MoveIssueToStageParams = Omittable<{
  issue: IssueRow;
  fromStageId: string;
  toStageId: string;
  newSortOrder: number;
  upperNeighborKey?: string;
}>;

type ReorderIssueParams = Omittable<{
  issue: IssueRow;
  stageId: string;
  upperNeighborKey?: string;
  lowerNeighborKey?: string;
  newSortOrder: number;
}>;

interface DeleteIssueParams {
  issue: IssueRow;
  stageId: string;
}

type DropIssuesParams = Omittable<{
  /** The dragged rows, ordered as they should land, top → bottom. */
  rows: IssueRow[];
  toStageId: string;
  /** Upper anchor of the drop slot (the block chains after it in cache). */
  upperNeighborKey?: string;
  /** Sort bounds of the drop slot — the block spreads its orders between them. */
  upperSortValue?: number;
  lowerSortValue?: number;
}>;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useKanbanMutations() {
  const [moveIssueMutation] = useMutation(MoveIssueToStageDocument);
  const [reorderIssueMutation] = useMutation(ReorderIssueDocument);
  const [bulkDropMutation] = useMutation(BulkDropIssuesToStageDocument);
  const [deleteIssueMutation] = useMutation(DeleteIssueDocument);

  const moveIssueToStage = useCallback(
    async ({
      issue,
      fromStageId,
      toStageId,
      newSortOrder,
      upperNeighborKey,
    }: MoveIssueToStageParams) => {
      await moveIssueMutation({
        variables: {
          issueId: issue.issueId,
          stageId: toStageId,
          sortOrder: newSortOrder,
        },
        optimisticResponse: {
          moveIssueToStage: {
            ...issue,
            sortOrder: newSortOrder,
            stageId: toStageId,
            assignees: issue.assignees ?? [],
          } as any,
        },
        update(cache) {
          removeIssueFromStageCache(cache, fromStageId, issue.issueId);
          insertIssueIntoStageCache(
            cache,
            toStageId,
            { issueId: issue.issueId, sortOrder: newSortOrder },
            upperNeighborKey,
          );
        },
      });
    },
    [moveIssueMutation],
  );

  const reorderIssue = useCallback(
    async ({
      issue,
      stageId,
      upperNeighborKey,
      lowerNeighborKey,
      newSortOrder,
    }: ReorderIssueParams) => {
      await reorderIssueMutation({
        variables: {
          issueId: issue.issueId,
          afterIssueId: upperNeighborKey ?? null,
          beforeIssueId: lowerNeighborKey ?? null,
        },
        optimisticResponse: {
          updateIssueSortOrder: {
            __typename: "Issue",
            id: issue.id,
            issueId: issue.issueId,
            sortOrder: newSortOrder,
          },
        },
        update(cache) {
          reorderIssueInStageCache(
            cache,
            stageId,
            issue.issueId,
            upperNeighborKey,
            lowerNeighborKey,
          );
        },
      });
    },
    [reorderIssueMutation],
  );

  /**
   * Lands a multi-card drop as ONE bulk mutation with ONE optimistic cache
   * write, so the whole selection appears in the target column on the same
   * frame — a per-row loop trickled the cards in as each call resolved. Sort
   * orders are spread client-side between the drop-slot bounds and sent as
   * explicit placements; the server writes them verbatim, so a row from any
   * source stage lands exactly where the block sits. Returns the rows the
   * server actually moved (its per-item edit filter may drop some).
   */
  const dropIssues = useCallback(
    async ({
      rows,
      toStageId,
      upperNeighborKey,
      upperSortValue,
      lowerSortValue,
    }: DropIssuesParams) => {
      const groupIds = new Set(rows.map((r) => r.issueId));
      // A drop boundary that is itself part of the dragged group vacates as
      // the group lands, so it cannot anchor the block — fall back to sorted
      // insertion. The sort VALUES stay: the vacating card's old sort still
      // bounds the interval the block spreads into.
      const afterIssueId =
        upperNeighborKey != null && !groupIds.has(upperNeighborKey)
          ? upperNeighborKey
          : undefined;
      let prevSort = upperSortValue;
      const placed = rows.map((issue) => {
        const sortOrder = calculateSortOrder(prevSort, lowerSortValue);
        prevSort = sortOrder;
        return { issue, sortOrder };
      });
      const result = await bulkDropMutation({
        variables: {
          stageId: toStageId,
          placements: placed.map((p) => ({
            issueId: p.issue.issueId,
            sortOrder: p.sortOrder,
          })),
        },
        optimisticResponse: {
          bulkMoveIssuesToStage: placed.map((p) => ({
            ...p.issue,
            stageId: toStageId,
            sortOrder: p.sortOrder,
            assignees: p.issue.assignees ?? [],
          })) as any,
        },
        update(cache, { data }) {
          // The server filters placements through its per-item edit check and
          // may move only a subset — land exactly the rows the result carries
          // (optimistic pass: all of them; final pass: the survivors), so a
          // refused row never shows a phantom move.
          const moved = new Set(
            (data?.bulkMoveIssuesToStage ?? []).map((r) => r.issueId),
          );
          dropIssuesIntoStageCache(cache, {
            orderedRows: placed
              .filter((p) => moved.has(p.issue.issueId))
              .map((p) => ({
                issueId: p.issue.issueId,
                sortOrder: p.sortOrder,
                fromStageId: p.issue.stageId,
              })),
            toStageId,
            afterIssueId,
          });
        },
      });
      return result.data?.bulkMoveIssuesToStage ?? [];
    },
    [bulkDropMutation],
  );

  const deleteIssue = useCallback(
    async ({ issue, stageId }: DeleteIssueParams) => {
      try {
        await deleteIssueMutation({
          variables: { issueId: issue.issueId },
          optimisticResponse: {
            deleteIssue: {
              __typename: "Issue",
              id: issue.id,
              issueId: issue.issueId,
              trashedAt: new Date().toISOString(),
            },
          },
          update(cache) {
            removeIssueFromStageCache(cache, stageId, issue.issueId);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this issue.
            const cacheId = cache.identify({
              __typename: "Issue",
              issueId: issue.issueId,
            });
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        toastManager.add({ title: "Project deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete project");
      }
    },
    [deleteIssueMutation],
  );

  return { moveIssueToStage, reorderIssue, dropIssues, deleteIssue };
}
